
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { HistoryEntityType } from '@/types/history';
import { useSettings } from '@/providers/SettingsProvider';

// New unified history record structure from the database
interface UnifiedHistoryRecord {
	id: string;
	operation: 'INSERT' | 'UPDATE' | 'DELETE';
	changed_fields: string[] | null;
	old_value: Record<string, any> | null;
	new_value: Record<string, any> | null;
	changed_by: string | null;
	changed_at: string;
	// Legacy fields for backward compatibility
	action?: string | null;
	description?: string | null;
	performed_by?: string | null;
}

// Display format for the UI
export interface HistoryItem {
	id: string;
	operation: string;
	changed_fields: string[];
	old_value: Record<string, any> | null;
	new_value: Record<string, any> | null;
	changed_by: string | null;
	changed_at: string;
	// Legacy support
	description?: string;
	performed_by?: string;
}

interface TableConfig {
	tableName: string;
	idField: string;
	joinTable?: string;
	joinField?: string;
}

export const useHistoryOperations = (entityType: HistoryEntityType) => {
	const [loading, setLoading] = useState(false);
	const [history, setHistory] = useState<HistoryItem[]>([]);
	const { dateFormat, timeFormat } = useSettings();

	// Get the table configuration based on entity type
	const getTableConfig = (type: HistoryEntityType): TableConfig => {
		switch (type) {
			case 'candidate':
				return {
					tableName: 'history_candidates',
					idField: 'candidate_id'
				};
			case 'job':
				return {
					tableName: 'history_jobs',
					idField: 'job_id'
				};
			case 'company':
				return {
					tableName: 'history_companies',
					idField: 'company_id'
				};
			case 'match':
				return {
					tableName: 'history_matches',
					idField: 'match_id'
				};
			case 'list':
				return {
					tableName: 'history_lists',
					idField: 'list_id'
				};
			case 'task':
				return {
					tableName: 'history_tasks',
					idField: 'task_id'
				};
			case 'event':
				return {
					tableName: 'history_events',
					idField: 'event_id'
				};
			case 'contact':
				return {
					tableName: 'history_contacts',
					idField: 'contact_id'
				};
			case 'query':
				return {
					tableName: 'history_saved_queries',
					idField: 'saved_query_id'
				};
		case 'workforce':
			return {
				tableName: 'history_workforce',
				idField: 'workforce_id'
			};

        default:
            throw new Error(`Unsupported entity type: ${type}`);
            
        }
    };

	const fetchHistory = useCallback(async (entityId: string) => {
		if (!entityId) return;

		try {
			setLoading(true);
			const { tableName, idField } = getTableConfig(entityType);

			const { data, error } = await supabase
				.from(tableName as any)
				.select('id, operation, changed_fields, old_value, new_value, changed_by, changed_at, action, description, performed_by')
				.eq(idField, entityId)
				.order('changed_at', { ascending: false });

			if (error) {
				console.error(`Error fetching ${entityType} history:`, error);
				throw error;
			}

			// Map the data to HistoryItem structure
			const historyItems: HistoryItem[] = (data || []).map((item: UnifiedHistoryRecord) => ({
				id: item.id,
				operation: item.operation || item.action || 'UPDATE',
				changed_fields: item.changed_fields || [],
				old_value: item.old_value,
				new_value: item.new_value,
				changed_by: item.changed_by || item.performed_by || null,
				changed_at: item.changed_at,
				// Include legacy fields for backward compatibility
				description: item.description,
				performed_by: item.performed_by || item.changed_by
			}));

			setHistory(historyItems);
		} catch (error) {
			console.error(`Error fetching ${entityType} history:`, error);
			setHistory([]);
		} finally {
			setLoading(false);
		}
	}, [entityType]);

	return {
		history,
		loading,
		fetchHistory
	};
};
